import "./_style.scss";
import { useContext, useEffect, useRef, useState } from "react";
import TkStepper from "../../components/atoms/TkStepper";
import TkBackButton from "../../components/molecules/TkBackButton";
import TkDataTable from "../../components/molecules/TkDataTable";
import { Box } from "@mui/material";
import TkInfoManual from "../../components/atoms/TkInfoManual";
import TkButton from "../../components/atoms/TkButton";

import { GlobalContext } from "../../context/globalContext";
import { infoHeadTitleList } from "./parts";
import { BusinessContext } from "../../context/businessContext";
import { useLocation, useNavigate } from "react-router";
import TkFileUpload from "../../components/molecules/TkFileUpload";
import TkContractorAndNumberLine from "../../components/molecules/TkContractorAndNumberLine";
import { httpIAPI_CON017, httpIAPI_CON019 } from "./api/request";
import authorityUserType from "../../assets/data/TkAuthorityProperty";
import { FormList, IAPI_CON017_Response } from "../../type/apiType/IAPI_CON017";
import TkDownloadLink from "../../components/atoms/TkDownloadLink";
import { labelids } from "../../assets/data/TkExplanationProperty";
import { TkRouteContext } from "../../context/tkRouteContext";
import { TkFileUploadPrevFiles } from "../../type/componentsType/TkFileUpload";
import { FormClassRetire, FormKbn } from "../../assets/data/Constants";
import TkMessageDialog from "../../components/molecules/TkMessageDialog";

/**
 * SCRC050115 直近3ヶ月の退職者一覧
 * @return SCRC050115 直近3ヶ月の退職者一覧
 */
const SCRC050115 = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { state, setState } = useContext(GlobalContext);
  const { state: businessState } = useContext<any>(BusinessContext);
  const [infoTableList, setInfoTableList] = useState<any>({
    infoHeadTitleList: infoHeadTitleList,
  });
  const [formList, setFormList] = useState<FormList[]>([]);
  const currentBusinessJob =
    businessState[
      businessState.common?.businessType?.toLowerCase().replace("-", "_")
    ];
  const { state: stateRouter, setState: setStateRouter } =
    useContext(TkRouteContext);
  const routerList = stateRouter.routerList || [];

  // 登録ボタンの活性化
  const [submitDisabled, setSubmitDisabled] = useState(false);
  const [showSubmitBtn, setShowSubmitBtn] = useState(true);

  // 登録ボタンを押した後、ダイアログの表示を制御する
  const [openDialog, setOpenDialog] = useState(false);

  const resRef = useRef<IAPI_CON017_Response>();

  useEffect(() => {
    const routerListInner = stateRouter.routerList || [];
    const href = routerListInner?.[routerListInner?.length - 2]?.href;
    const isNotShow =
      href?.includes("SCRW050002") ||
      href?.includes("SCRW050003") ||
      href?.includes("SCRW050009");
    setShowSubmitBtn(!isNotShow);
  }, [stateRouter]);

  // 初期化
  const handleInit = async () => {
    try {
      const res = await httpIAPI_CON017({
        applyId: currentBusinessJob?.applyId,
        contractorNo:
          state.authInfo?.userType === authorityUserType.userTypeNumber.KIKO
            ? businessState.common?.contractorNo
            : state.authInfo?.contractorNo,
        facilityNo: location.state?.facilityNo || "",
      });

      if (res.resultCd === "I0000") {
        resRef.current = res;
        const tempFormList: FormList[] = [];

        res.formList?.forEach((file) => {
          if (
            file?.formClass === FormClassRetire["雇用保険資格喪失証明書等"] &&
            file?.formKbn === FormKbn["退職書類"] &&
            Array.isArray(res.facilityRetireList) &&
            res.facilityRetireList.some(
              (retire) => retire?.staffNo === file?.staffNo
            )
          ) {
            tempFormList.push({
              ...file,
            });
          }
        });
        setFormList(tempFormList);

        setState({ ...state, errorMsgId: "" });
      } else {
        setState({
          ...state,
          errorMsgId: res.resultCd,
        });
      }
    } catch (error) {
      navigate("/error");
    }
  };

  useEffect(() => {
    handleInit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const infoBodyList = getTableBodyList(resRef.current!, formList);
    const data = {
      infoHeadTitleList: infoHeadTitleList,
      infoBodyList: infoBodyList,
    };
    setInfoTableList(data);
    setSubmitDisabled(
      () =>
        formList?.filter?.((obj) => obj.fileName !== "")?.length <
        (resRef.current?.facilityRetireList?.length ?? 0)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formList]);

  function handleUploadFile(
    args: TkFileUploadPrevFiles,
    flag?: string,
    staffNo?: string
  ) {
    const {
      target: { value, id },
    } = args;

    if (flag === "remove") {
      setFormList((prev) => {
        return prev?.map((item) =>
          item?.staffNo === staffNo
            ? { ...item, fileName: "", fileBase64: "", staffNo: "" }
            : item
        );
      });
      return;
    } else if (value == null) {
      // TkFileUploadコンポーネントはマントされた時点で、onChangeを呼ぶから、ここで受け止める
      return;
    } else {
      setFormList((prev) => {
        const tempFormList = [...prev];

        if (
          prev[Number(id)]?.fileName === value?.[0]?.formName &&
          prev[Number(id)]?.fileBase64 === value?.[0]?.fileBase64
        ) {
          return prev;
        }

        tempFormList[Number(id)] = {
          ...tempFormList[Number(id)],
          fileName: value?.[0]?.formName,
          fileBase64: value?.[0]?.fileBase64,
          staffNo: staffNo,
          serialNo: prev[Number(id)]?.serialNo ?? Number(id) + 1,
        };
        return tempFormList;
      });
    }
  }

  /**
   * 施設一覧テーブルを作成する
   * @param res
   * @param formList
   * @returns infoBodyList
   */
  function getTableBodyList(res: IAPI_CON017_Response, formList: FormList[]) {
    const infoBodyList: any[] = [];

    res?.facilityRetireList?.forEach((facility, index) => {
      const file: FormList | undefined = formList?.find?.(
        (f) => f?.staffNo === facility?.staffNo
      );
      const data = [
        {
          children:
            (facility.facilityNo ?? "") + ":" + (facility.facilityName ?? ""), // 施設番号:施設名称
          forSort: facility.facilityNo,
        },
        {
          children:
            (facility.staffNo ?? "") + ":" + (facility.staffNameKanji ?? ""),
          forSort: facility.staffNo,
        },
        {
          children: facility.birthday,
          forSort: facility.birthday,
          align: "center",
        },
        {
          children: facility.retireDate,
          forSort: facility.retireDate,
          align: "center",
        },
        {
          children: showSubmitBtn ? (
            <TkFileUpload
              id={index}
              wantToSetValue={
                file?.fileName && file?.fileBase64
                  ? [
                      {
                        formName: file?.fileName ?? "",
                        fileBase64: file?.fileBase64 ?? "",
                      },
                    ]
                  : []
              }
              onChange={(args, type) =>
                handleUploadFile(args, type, facility.staffNo)
              }
            />
          ) : (
            <TkDownloadLink
              dataname={file?.fileName!}
              base64filedata={file?.fileBase64!}
              downloadBase64
            ></TkDownloadLink>
          ),
          forSort: facility.retireDate,
        },
      ];
      infoBodyList.push(data);
    });
    return infoBodyList;
  }

  //登録ボタン用イベント
  const handleGoNext = async () => {
    try {
      const reqformList: FormList[] = JSON.parse(JSON.stringify(formList))
        ?.filter?.(Boolean)
        ?.filter?.((item: FormList) => item?.fileBase64 && item?.fileName);

      reqformList?.forEach((item) => {
        if (!item) {
          item = {};
        }
        item.facilityNo =
          businessState.job_16?.facilityNo || location.state?.facilityNo;
        item.staffNo = item.staffNo ?? "";
        item.formKbn = "6";
        if (item.fileBase64) {
          item.formClass = FormClassRetire["雇用保険資格喪失証明書等"];
        }
      });
      const res = await httpIAPI_CON019({
        applyId: currentBusinessJob?.applyId,
        businessType: businessState.common?.businessType,
        contractorNo:
          state.authInfo?.userType === authorityUserType.userTypeNumber.KIKO
            ? businessState.common?.contractorNo
            : state.authInfo?.contractorNo,
        formList: reqformList,
        screenId: "SCRC050115",
      });
      if (res.resultCd === "I0000") {
        setState({
          ...state,
          errorMsgId: "",
        });
        if (routerList.length > 1) {
          setStateRouter({
            ...state,
            routerList: [...routerList.slice(0, routerList.length - 1)],
          });
          setOpenDialog(false);
          navigate(`${routerList[routerList.length - 2].href}`);
        } else {
          setStateRouter({
            ...state,
            routerList: [...[]],
          });
          setOpenDialog(false);
          navigate("/error");
        }
      } else {
        setState({
          ...state,
          errorMsgId: res.resultCd,
        });
        setOpenDialog(false);
      }
    } catch (error) {
      setOpenDialog(false);
      navigate("/error");
    }
  };

  return (
    <Box data-testid="SCRC050115PageForTest" className="SCRC050115">
      <TkStepper steps={[]} activeStep={1} savaStep={businessState[""] - 1} />
      <TkInfoManual rule={"<"}>{labelids.labelid.LBLC050115_01}</TkInfoManual>
      {state.authInfo?.userType === authorityUserType.userTypeNumber.KIKO && (
        <TkContractorAndNumberLine
          name="契約者（法人）名"
          no={`${businessState.common?.contractorNo ?? ""}:${
            businessState.common?.contractorName ?? ""
          }`}
        />
      )}
      <TkDataTable
        bodyList={
          infoTableList?.infoBodyList &&
          infoTableList.infoBodyList.map((row: any[]) =>
            row.map((item: any) => ({ ...item, forSort: item?.forSort ?? 0 }))
          )
        }
        defaultRowsPerPage={5}
        headList={infoTableList?.infoHeadTitleList}
      />

      <Box className="buttonGroup">
        <TkBackButton />
        {showSubmitBtn && (
          <TkButton
            disabled={submitDisabled}
            onClick={() => setOpenDialog(true)}
          >
            登録
          </TkButton>
        )}
      </Box>

      <TkMessageDialog
        open={openDialog}
        contextText="　入力した内容で登録を行いますが、問題ないでしょうか。"
        title="確認（情報を登録します）"
        onOky={() => {
          handleGoNext();
        }}
        onCancel={() => {
          setOpenDialog(false);
        }}
        okText="はい"
        cancelText="いいえ"
      />
    </Box>
  );
};

export default SCRC050115;
